import React, { useState, useEffect } from 'react';
import calendarService from '../../services/calendarService.js';
import userService from '../../services/userService.js';
import './WeekendDuty.css';

const WeekendDuty = () => {
    const [weekendDuties, setWeekendDuties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [memberSelections, setMemberSelections] = useState({}); // Track member selection for each weekend
    const [availableMembers, setAvailableMembers] = useState([]); // Will be populated from API

    // Generate weekend dates for the next 6 months
    const generateWeekendDates = () => {
        const dates = [];
        const today = new Date();
        const sixMonthsFromNow = new Date(today.getFullYear(), today.getMonth() + 6, today.getDate());

        for (let d = new Date(today); d <= sixMonthsFromNow; d.setDate(d.getDate() + 1)) {
            if (d.getDay() === 5) { // Friday
                const friday = new Date(d);
                const saturday = new Date(d.getTime() + (1 * 24 * 60 * 60 * 1000));
                const sunday = new Date(d.getTime() + (2 * 24 * 60 * 60 * 1000));

                const weekendData = {
                    date: friday.toISOString().split('T')[0],
                    friday: friday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
                    saturday: saturday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
                    sunday: sunday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
                    fullRange: `${friday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${sunday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                };

                dates.push(weekendData);
            }
        }
        return dates;
    };

    const weekendDates = generateWeekendDates();

    // API functions
    const fetchWeekendDuties = async () => {
        try {
            setLoading(true);
            // Use the calendarService to get all events and filter for weekend duties
            const allEvents = await calendarService.getAllEvents();

            // Filter for weekend duty events
            const weekendDutyEvents = (allEvents || []).filter(event => {
                // Check if it's explicitly marked as a weekend duty
                if (event.isWeekendDuty === true || event.eventType === 'weekend_duty') {
                    return true;
                }

                // Check if it has weekend duty specific fields
                if (event.assignedMemberId || event.weekendDate) {
                    return true;
                }

                // Check if it's a weekend duty by subject
                if (event.Subject === 'Weekend Duty') {
                    return true;
                }

                // Check if it's a weekend duty by description
                if (event.Description && event.Description.includes('Weekend duty')) {
                    return true;
                }

                return false;
            });
            setWeekendDuties(weekendDutyEvents);
            setError(''); // Clear any previous errors
        } catch (err) {
            setError(`Error fetching weekend duties: ${err.message}`);
            console.error('Error fetching weekend duties:', err);
        } finally {
            setLoading(false);
        }
    };

    const createWeekendDuty = async (weekendDate, assignedMemberId) => {
        try {
            // Create weekend duty as a calendar event
            // Use proper all-day format: same date for start and end with IsAllDay=true
            const fridayDate = new Date(weekendDate + 'T00:00:00');
            const sundayDate = new Date(fridayDate.getTime() + (2 * 24 * 60 * 60 * 1000)); // Sunday (2 days later)

            const weekendDutyEvent = {
                Subject: 'Weekend Duty',
                StartTime: fridayDate.toISOString().split('T')[0] + 'T00:00:00.000Z', // Friday 00:00 UTC
                EndTime: sundayDate.toISOString().split('T')[0] + 'T00:00:00.000Z', // Sunday 00:00 UTC (same time for all-day)
                IsAllDay: true, // Set to true for proper all-day display
                Description: 'Weekend duty assignment',
                FamilyId: parseInt(assignedMemberId),
                eventType: 'weekend_duty',
                isAppointment: false,
                isWeekendDuty: true,
                assignedMemberId: parseInt(assignedMemberId),
                weekendDate: weekendDate, // Store the original weekend date
                eventColor: availableMembers.find(m => m.id === parseInt(assignedMemberId))?.color || '#4FB06D'
            };

            // Use the calendarService to create the event
            await calendarService.createEvent(weekendDutyEvent);

            await fetchWeekendDuties(); // Refresh the list
            return true;
        } catch (err) {
            setError(`Error creating weekend duty: ${err.message}`);
            console.error('Error creating weekend duty:', err);
            return false;
        }
    };

    const updateWeekendDuty = async (id, weekendDate, assignedMemberId) => {
        try {
            // Update weekend duty event
            // Use proper all-day format: same date for start and end with IsAllDay=true
            const fridayDate = new Date(weekendDate + 'T00:00:00');
            const sundayDate = new Date(fridayDate.getTime() + (2 * 24 * 60 * 60 * 1000)); // Sunday (2 days later)

            const updateData = {
                Subject: 'Weekend Duty',
                StartTime: fridayDate.toISOString().split('T')[0] + 'T00:00:00.000Z', // Friday 00:00 UTC
                EndTime: sundayDate.toISOString().split('T')[0] + 'T00:00:00.000Z', // Sunday 00:00 UTC (same time for all-day)
                IsAllDay: true, // Set to true for proper all-day display
                Description: 'Weekend duty assignment',
                FamilyId: parseInt(assignedMemberId),
                eventType: 'weekend_duty',
                isAppointment: false,
                isWeekendDuty: true,
                assignedMemberId: parseInt(assignedMemberId),
                weekendDate: weekendDate, // Store the original weekend date
                eventColor: availableMembers.find(m => m.id === parseInt(assignedMemberId))?.color || '#4FB06D'
            };

            // Use the calendarService to update the event
            await calendarService.updateEvent(id, updateData);

            await fetchWeekendDuties(); // Refresh the list
            return true;
        } catch (err) {
            setError(`Error updating weekend duty: ${err.message}`);
            console.error('Error updating weekend duty:', err);
            return false;
        }
    };

    const deleteWeekendDuty = async (id) => {
        try {
            // Use the calendarService to delete the event
            await calendarService.deleteEvent(id);

            await fetchWeekendDuties(); // Refresh the list
            return true;
        } catch (err) {
            setError(`Error deleting weekend duty: ${err.message}`);
            console.error('Error deleting weekend duty:', err);
            return false;
        }
    };

    // Load weekend duties and users on component mount
    useEffect(() => {
        fetchWeekendDuties();
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            console.log('Fetching users from API...');
            const users = await userService.getWeekendDutyUsers();
            console.log('Users received from API:', users);
            setAvailableMembers(users);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError(`Error loading family members: ${err.message}`);
        }
    };

    // Robust helper to check if a duty covers a given weekend date string (YYYY-MM-DD)
    const isWeekendDutyForDate = (duty, weekendDateStr) => {
        if (!duty) return false;

        // Check explicit weekendDate string or Date object
        if (duty.weekendDate) {
            let dutyDateStr = typeof duty.weekendDate === 'string'
                ? duty.weekendDate.split('T')[0]
                : duty.weekendDate.toISOString().split('T')[0];

            if (dutyDateStr === weekendDateStr) return true;

            // Check if stored date was shifted by -1 day
            const adjustedDateMinus = new Date(dutyDateStr);
            adjustedDateMinus.setDate(adjustedDateMinus.getDate() - 1);
            if (adjustedDateMinus.toISOString().split('T')[0] === weekendDateStr) return true;

            // Check if stored date was shifted by +1 day
            const adjustedDatePlus = new Date(dutyDateStr);
            adjustedDatePlus.setDate(adjustedDatePlus.getDate() + 1);
            if (adjustedDatePlus.toISOString().split('T')[0] === weekendDateStr) return true;
        }

        // Check StartTime string or Date object
        if (duty.StartTime) {
            let startTimeStr = typeof duty.StartTime === 'string'
                ? duty.StartTime.split('T')[0]
                : duty.StartTime.toISOString().split('T')[0];

            if (startTimeStr === weekendDateStr) return true;

            const eventStartDate = new Date(duty.StartTime);
            const targetDate = new Date(weekendDateStr);
            if (eventStartDate.toDateString() === targetDate.toDateString()) return true;
        }

        return false;
    };

    const handleAssignDuty = async (weekendDate) => {
        const targetMember = memberSelections[weekendDate];

        if (!targetMember) {
            setError('Please select a family member for this weekend');
            return;
        }

        // Check if duty already exists for this weekend
        const existingDuty = weekendDuties.find(duty => isWeekendDutyForDate(duty, weekendDate));

        if (existingDuty) {
            // Update existing duty
            const success = await updateWeekendDuty(existingDuty.id, weekendDate, targetMember);
            if (success) {
                setMemberSelections(prev => ({
                    ...prev,
                    [weekendDate]: ''
                }));
                setError('');
            }
        } else {
            // Create new duty
            const success = await createWeekendDuty(weekendDate, targetMember);
            if (success) {
                setMemberSelections(prev => ({
                    ...prev,
                    [weekendDate]: ''
                }));
                setError('');
            }
        }
    };

    const handleClearAssignment = async (weekendDate) => {
        const duty = weekendDuties.find(d => isWeekendDutyForDate(d, weekendDate));
        if (duty) {
            await deleteWeekendDuty(duty.id);
        }
    };

    const handleAutoAssign = async () => {
        const unassignedWeekends = weekendDates.filter(weekend =>
            !weekendDuties.find(duty => isWeekendDutyForDate(duty, weekend.date))
        );

        if (unassignedWeekends.length === 0) {
            setError('All weekends are already assigned');
            return;
        }

        // Round-robin assignment
        for (let i = 0; i < unassignedWeekends.length; i++) {
            const memberIndex = i % availableMembers.length;
            const member = availableMembers[memberIndex];
            await createWeekendDuty(unassignedWeekends[i].date, member.id);
        }
    };

    const handleClearAll = async () => {
        if (window.confirm('Are you sure you want to clear all weekend duty assignments?')) {
            for (const duty of weekendDuties) {
                await deleteWeekendDuty(duty.id);
            }
        }
    };

    // Calculate statistics
    const getMemberStats = (memberId) => {
        return weekendDuties.filter(duty =>
            (duty.assignedMemberId === memberId) || (duty.FamilyId === memberId)
        ).length;
    };

    const getMemberName = (memberId) => {
        const member = availableMembers.find(m => m.id === memberId);
        return member ? member.display_name : 'Unknown';
    };

    const getMemberColor = (memberId) => {
        const member = availableMembers.find(m => m.id === memberId);
        return member ? member.color : '#ccc';
    };

    if (loading) {
        return (
            <div className="weekend-duty" style={{ textAlign: 'center', padding: '50px' }}>
                <div style={{
                    display: 'inline-block',
                    width: '50px',
                    height: '50px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #667eea',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ marginTop: '20px', fontSize: '1.6rem', color: '#666' }}>
                    Loading weekend duties...
                </p>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Calculate total assigned count for progress bar
    const assignedCount = weekendDates.filter(weekend =>
        weekendDuties.some(d => isWeekendDutyForDate(d, weekend.date))
    ).length;

    const progressPercent = Math.round((assignedCount / (weekendDates.length || 1)) * 100);

    return (
        <div className="weekend-duty">
            <div className="weekend-duty-header">
                <h2>🏠 Weekend Duty Allocation</h2>
                <p>Manage weekend duty assignments for family members</p>

                {/* Progress Bar Container */}
                <div className="duty-progress-container">
                    <div className="progress-label-wrap">
                        <span>Assignment Progress</span>
                        <span className="progress-value">{assignedCount} / {weekendDates.length} ({progressPercent}%)</span>
                    </div>
                    <div className="progress-track">
                        <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
                    </div>
                </div>

                <div className="action-buttons">
                    <button onClick={handleAutoAssign} className="auto-assign-btn">
                        🎯 Auto-Assign All
                    </button>
                    <button onClick={handleClearAll} className="clear-btn">
                        🗑️ Clear All Assignments
                    </button>
                </div>
            </div>

            {error && <div className="error-message" style={{
                background: '#f8d7da',
                color: '#721c24',
                padding: '15px',
                borderRadius: '8px',
                margin: '20px',
                textAlign: 'center',
                border: '1px solid #f5c6cb'
            }}>{error}</div>}

            <div className="duty-stats">
                <h3>📊 Duty Distribution</h3>
                <div className="stats-grid">
                    {availableMembers.length > 0 ? (
                        availableMembers.map(member => (
                            <div key={member.id} className="stat-card" style={{ borderLeftColor: member.color || '#4f46e5' }}>
                                <div className="stat-name">{member.display_name}</div>
                                <div className="stat-count" style={{ color: member.color || '#4f46e5' }}>{getMemberStats(member.id)}</div>
                            </div>
                        ))
                    ) : (
                        <div className="stat-card">
                            <div className="stat-name">Loading...</div>
                            <div className="stat-count">-</div>
                        </div>
                    )}
                </div>
                {availableMembers.length === 0 && (
                    <p style={{ textAlign: 'center', color: '#666', marginTop: '10px' }}>
                        No family members loaded. Check console for errors.
                    </p>
                )}
            </div>

            <div className="weekend-list">
                <h3>📅 Weekend Duty Assignments</h3>
                <div className="weekends-grid">
                    {weekendDates.map(weekend => {
                        const duty = weekendDuties.find(d => isWeekendDutyForDate(d, weekend.date));

                        const assignedMemberId = duty ? (duty.assignedMemberId || duty.FamilyId) : null;
                        const assignedColor = assignedMemberId ? getMemberColor(assignedMemberId) : null;
                        const assignedName = assignedMemberId ? getMemberName(assignedMemberId) : null;
                        const initials = assignedName ? assignedName.split('&').map(s => s.trim()[0]).join('') : '';

                        return (
                            <div 
                                key={weekend.date} 
                                className={`weekend-card ${duty ? 'assigned' : 'unassigned'}`}
                                style={assignedColor ? { borderLeft: `5px solid ${assignedColor}` } : {}}
                            >
                                <div className="weekend-dates">
                                    {weekend.fullRange}
                                </div>
                                {duty ? (
                                    <div className="assigned-member">
                                        <div
                                            className="member-avatar"
                                            style={{ backgroundColor: assignedColor }}
                                        >
                                            {initials}
                                        </div>
                                        <span>{assignedName}</span>
                                        <button
                                            onClick={() => handleClearAssignment(weekend.date)}
                                            className="remove-btn"
                                            title="Remove Assignment"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <div className="assign-member">
                                        <select
                                            value={memberSelections[weekend.date] || ''}
                                            onChange={(e) => {
                                                setMemberSelections(prev => ({
                                                    ...prev,
                                                    [weekend.date]: e.target.value
                                                }));
                                                if (e.target.value) {
                                                    setError('');
                                                }
                                            }}
                                            className="member-select"
                                        >
                                            <option value="">Select Member</option>
                                            {availableMembers.map(member => (
                                                <option key={member.id} value={member.id}>
                                                    {member.display_name}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => handleAssignDuty(weekend.date)}
                                            className="assign-btn"
                                            disabled={!memberSelections[weekend.date]}
                                        >
                                            Assign
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default WeekendDuty;


